import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {useCachedPropertyGroupsController} from "../../../../../../hooks/use-industry";
import {createUUId, numComparator} from "../../../../../../../core/services/utils/utils";
import axios from "axios";
import Api from "../../../../../../../core/services/api/api";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes} from "../../../../../../../packages/data-grid";
import {ProductPropertyGroupRecordLeafTypes} from "../../../../../../../core/constants/enums";
import {Box, Button, Typography} from "@mui/material";
import {UnfoldLessRounded, UnfoldMoreRounded} from "@mui/icons-material";
import PartnerDataGrid from "../../../../../../containers/data-grid";

const PropertyGroupInformationSection = ({partId, groupId, cache, updateCache}) => {
    const [expandingAll, setExpandingAll] = useState(false);
    const [collapsingAll, setCollapsingAll] = useState(false);
    const [structureSchema, setStructureSchema] = useState([]);
    const [records, setRecords] = useState([]);

    const isMounted = useIsMounted();
    const {getPropertyGroupAttributes} = useCachedPropertyGroupsController();

    const groupCache = useMemo(() => cache['property-group-' + groupId], [cache, groupId]);
    const schemaCache = useMemo(() => cache['property-group-' + groupId + '-schema'], [cache, groupId]);

    const [loadingRecords, setLoadingRecords] = useState(!groupCache);
    const [loadingSchema, setLoadingSchema] = useState(!schemaCache);

    /**
     * Fetches the structure of the property group.
     * @type {(function(*): Promise<void>)}
     */
    const getPropertyGroupStructure = useCallback(async (cancelTokenSource) => {
        setLoadingSchema(true);
        const attributes = await getPropertyGroupAttributes(groupId, cancelTokenSource.token)
        if (!isMounted())
            return;
        if (!cancelTokenSource?.token?.reason) {
            setLoadingSchema(false);
        }
        if (!!attributes) {
            const parsedStructureSchema = [...(attributes ?? [])];
            parsedStructureSchema.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
            const schema = parsedStructureSchema.map((e, i, arr) => ({
                groupPropertyId: e.id,
                title: e.title,
                propertyName: e.name,
                nextPropertyName: arr[i + 1]?.name ?? null,
                type: e.industryGroupPropertyAttrTypeId,
                isRequired: e.isRequired,
                nullReplacement: e.nullReplacement,
            }))
            setStructureSchema(schema);
            updateCache('property-group-' + groupId + '-schema', schema);
        }
    }, [getPropertyGroupAttributes, groupId, isMounted, updateCache])

    /**
     * As soon as the component mounts:
     * - fetches the structure of the property group.
     */
    useLayoutEffect(() => {
        if (schemaCache) {
            setStructureSchema(schemaCache);
            setLoadingSchema(false);
            return;
        }
        const axiosCancelTokenSource = axios.CancelToken.source();
        void getPropertyGroupStructure(axiosCancelTokenSource);
        return () => axiosCancelTokenSource.cancel();
    }, [getPropertyGroupStructure, schemaCache]);


    /**
     * Fetches the records of the property group.
     * @type {(function(*): Promise<void>)}
     */
    const getPropertyGroupRecords = useCallback(async () => {
        setLoadingRecords(true);
        const response = await Api.listPartIndustryGroupProperties(groupId, partId);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingRecords(false);
        }
        if (response?.resultFlag) {
            const records = response?.data?.map(e => ({
                ...e,
                data: e.content && e.content?.trim()?.length ? JSON.parse(e.content) : {},
                _newRecordPropertyNames: null, // an array of names that would suggest which properties to make editable
                _newRecordValues: [],// keys of properties to be edited, and their new values
            })) ?? []
            setRecords(records);
            updateCache('property-group-' + groupId, records);
        }
    }, [groupId, partId, isMounted, updateCache])


    /**
     * Fetches the records of the property group.
     */
    useEffect(() => {
        if (groupCache) {
            setRecords(groupCache);
            setLoadingRecords(false);
            return;
        }
        const axiosCancelTokenSource = axios.CancelToken.source();
        void getPropertyGroupRecords(axiosCancelTokenSource.token);
        return () => axiosCancelTokenSource.cancel();
    }, [groupCache, getPropertyGroupRecords, groupId])

    /**
     * Groups the records based on the schema.
     */
    const groupRecords = useCallback((records, schema, currentLevel = 0, parentCommonId = '') => {
        if (currentLevel >= schema.length)
            return records; // Base case: return records if schema is exhausted

        const {propertyName, nextPropertyName} = schema[currentLevel];

        let newRecords = [];
        // Create a map to hold the grouped records
        const groupsMap = records.reduce((acc, record) => {
            const groupValue = record.data[propertyName]; // Group by the title of the current property
            if (!groupValue && record._newRecordPropertyNames) {
                newRecords.push(record);
                return acc;
            }

            if (!acc[groupValue]) {
                const commonId = parentCommonId ? `${parentCommonId}-${groupValue}` : ((groupValue?.toString()) ?? schema[currentLevel]?.nullReplacement ?? createUUId());
                acc[groupValue] = {
                    commonId: commonId,
                    schema: schema[currentLevel],
                    value: groupValue,
                    approved: record.approved,
                    children: [],
                    counts: {}, // Dynamic counts object to hold counts for inner properties only
                    titles: {},
                };
            }

            acc[groupValue].children.push(record);

            return acc;
        }, {});

        // Now, for each group, recursively group by the next property
        const groups = Object.values(groupsMap).map(group => {

            if (!schema[currentLevel + 1]?.nextPropertyName) {
                // parent of leaves
                return {
                    commonId: group.commonId, // Pass the commonId along
                    schema: group.schema,
                    value: group.value,
                    approved: group.approved,
                    records: group.children, // list of records
                    counts: {
                        [`${nextPropertyName}Count`]: group.children.length,
                    }, // Store the count for the leaf level
                    titles: {
                        [`${nextPropertyName}Title`]: [...new Set(group.children.map(e => e.data[nextPropertyName]))],
                    },
                };
            }

            const groupedChildren = groupRecords(group.children, schema, currentLevel + 1, group.commonId) // Recursive call for the next level

            // Collect counts dynamically based on schema depth (excluding the current level)
            const counts = groupedChildren.reduce((totalCounts, child) => {
                // Sum counts from child groups if available
                if (child.counts) {
                    for (const countKey in child.counts) {
                        totalCounts[countKey] = (totalCounts[countKey] || 0) + child.counts[countKey];
                    }
                }
                if (child.records) {
                    const lastPropertyName = schema[schema.length - 1]?.propertyName ?? "";
                    totalCounts[`${lastPropertyName}Count`] = (totalCounts[`${lastPropertyName}Count`] || 0) + child.records.length;
                }
                return totalCounts;
            }, {});

            counts[`${nextPropertyName}Count`] = groupedChildren.length;

            // Collect counts dynamically based on schema depth (excluding the current level)
            const titles = groupedChildren.reduce((totalTitles, child) => {
                // Sum counts from child groups if available
                if (child.titles) {
                    for (const countKey in child.titles) {
                        totalTitles[countKey] = [...new Set([...(totalTitles[countKey] ?? []), ...child.titles[countKey]])];
                    }
                }
                return totalTitles;
            }, {});

            titles[`${nextPropertyName}Title`] = [...new Set(group.children.map(e => e.data[nextPropertyName]))];

            return {
                commonId: group.commonId,
                schema: group.schema,
                value: group.value,
                approved: group.approved,
                counts: counts, // Store the calculated counts for inner levels
                titles: titles,
                children: groupedChildren, // Grouped children or records
            };
        });

        if (newRecords) {
            return [...groups, ...newRecords];
        }

        return groups;
    }, []);

    const groupedRecords = useMemo(() => groupRecords(records, structureSchema), [groupRecords, records, structureSchema]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...structureSchema.map((e) => ({
                name: e.propertyName,
                title: e.title,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.left,
                pinned: false,
                sortable: false,
                resizable: false,
                pinnedToggleable: false,
                reorderable: false,
                visibilityToggleable: false,
                savable: false,
                width: 180,
            })),
            ...structureSchema.map((e) => ({
                name: `__${e.propertyName}`,
                visible: false,
                pinned: false,
                sortable: false,
                resizable: false,
                pinnedToggleable: false,
                reorderable: false,
                visibilityToggleable: false,
                savable: false,
                width: 0,
            })),
        ], [structureSchema])

    /**
     * Groups the range records.
     * @type {(function(*, *): ([]|*[]))}
     */
    const groupRangeRecords = useCallback((_records, schema) => {
        if (!_records.length)
            return [];

        const records = _records.map(e => ({
            ...e,
            data: {
                ...e.data,
                [schema.propertyName]: (e.data[schema.propertyName] === null && !schema.isRequired) ? schema.nullReplacement : Number(e.data[schema.propertyName]),
            }
        }));
        records.sort((a, b) => {
            if (typeof a === 'string')
                return -1;
            if (typeof b === 'string')
                return 1;
            return numComparator(a.data[schema.propertyName], b.data[schema.propertyName])
        });

        const nullValues = records.filter(e => typeof e.data[schema.propertyName] !== 'number');
        const sequences = [];
        if (nullValues.length) {
            sequences.push(nullValues);
        }
        const nonNullValues = records.filter(e => typeof e.data[schema.propertyName] === 'number');
        let currentSequence
        if (nonNullValues.length) {
            currentSequence = [nonNullValues[0]];

            for (let i = 1; i < nonNullValues.length; i++) {
                // Check if the current number is consecutive to the last one in the sequence
                if (nonNullValues[i].data[schema.propertyName] === nonNullValues[i - 1].data[schema.propertyName] + 1) {
                    currentSequence.push(nonNullValues[i]);
                } else {
                    // If not consecutive, add the current sequence to the result and start a new one
                    sequences.push(currentSequence);
                    currentSequence = [nonNullValues[i]];
                }
            }
        }
        // Add the last sequence to the result
        if (currentSequence) {
            sequences.push(currentSequence);
        }
        return sequences;
    }, [])

    const dataGridRows = useMemo(() => {
        /**@type {DataGridRow[]}*/
        const flatArray = [];

        // Recursive function to traverse and flatten the grouped records
        function processGroup(group, parents = [], forceLeaf) {
            const isLeaf = !!forceLeaf || !group.commonId; // Check if this is a leaf node (a record)

            const defaultRecord = typeof group?.value?.content === 'string';

            // Prepare the cells object for the current group or record
            const cells = {};

            // Populate cells based on whether it's a group or record
            if (isLeaf) {
                const record = group;
                if (forceLeaf && !!group.schema) {
                    switch (group.schema.type) {
                        case ProductPropertyGroupRecordLeafTypes.range: {
                            const rangeRecords = group.value;
                            for (const parent of parents) {
                                if (parent?.propertyName) {
                                    cells[`__${parent.propertyName}`] = {groupValue: parent.value};
                                    cells[parent.propertyName] = <p {...dataGridCellTextProps}/>;
                                }
                            }
                            if (rangeRecords.length === 0) {
                                cells[group.schema.propertyName] = '';
                            } else if (rangeRecords.length === 1) {
                                cells[group.schema.propertyName] = rangeRecords[0].data[group.schema.propertyName] === null ? (group.schema.nullReplacement ?? "") : rangeRecords[0].data[group.schema.propertyName] ?? ""
                            } else {
                                cells[group.schema.propertyName] =
                                    <div className={'d-flex align-items-center font-weight-normal'} style={{gap: 4}}
                                    >
                                        {rangeRecords[0].data[group.schema.propertyName]}
                                        <Typography
                                            variant={'h5'}
                                            className={'px-2 font-weight-bold'}
                                            sx={{color: 'primary.main', height: 14, lineHeight: 0}}
                                        >
                                            ...
                                        </Typography>
                                        {rangeRecords[rangeRecords.length - 1].data[group.schema.propertyName]}
                                    </div>;
                            }
                            break;
                        }
                        default: {
                            for (const parent of parents) {
                                if (parent?.propertyName) {
                                    cells[`__${parent.propertyName}`] = {groupValue: parent.value};
                                    cells[parent.propertyName] = <p {...dataGridCellTextProps}/>;
                                }
                            }
                            for (const schema of structureSchema) {
                                if (!(`__${schema.propertyName}` in cells)) {
                                    cells[`__${schema.propertyName}`] = {groupValue: group.value.data[schema.propertyName]};
                                }
                                if (schema.propertyName === group.schema.propertyName) {
                                    const shownValue = group.value.data[schema.propertyName] ?? (schema.nullReplacement ?? '');
                                    // Display the group's title in its corresponding column
                                    cells[schema.propertyName] =
                                        <div className={'d-flex align-items-center justify-content-end'}>
                                            <p {...dataGridCellTextProps} className={'mr-2'}>{shownValue}</p>
                                        </div>;
                                } else {
                                    cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                                }
                            }
                            break;
                        }
                    }
                } else {
                    // this is for new cells.
                    for (const schema of structureSchema) {
                        cells[`__${schema.propertyName}`] = {groupValue: record.data[schema.propertyName]};
                        cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                    }
                    const lastSchema = structureSchema[structureSchema.length - 1];
                    if (lastSchema) {
                        cells[lastSchema.propertyName] =
                            <div className={'d-flex align-items-center'}>
                                <p {...dataGridCellTextProps}>{record.data[lastSchema.propertyName]}</p>
                            </div>;
                    }
                }
            } else {
                for (const parent of parents) {
                    if (parent?.propertyName) {
                        cells[`__${parent.propertyName}`] = {groupValue: parent.value};
                    }
                }
                // If it's a group, display the group's title in its column, and show counts for inner groups
                for (const schema of structureSchema) {
                    if (!(`__${schema.propertyName}` in cells)) {
                        cells[`__${schema.propertyName}`] = {groupValue: group.value};
                    }
                    if (schema.propertyName === group.schema.propertyName) {
                        const shownValue = group.value ?? (schema.nullReplacement ?? '');
                        // Display the group's title in its corresponding column
                        cells[schema.propertyName] =
                            <div className={'d-flex align-items-center justify-content-end'}>
                                <p {...dataGridCellTextProps} className={'mr-2'}>{shownValue}</p>
                            </div>;
                    } else if (group.counts[`${schema.propertyName}Count`] !== undefined) {
                        const count = group.counts[`${schema.propertyName}Count`];
                        const titles = group.titles[`${schema.propertyName}Title`];
                        const titleGroups = schema.type === ProductPropertyGroupRecordLeafTypes.range
                            ? groupRangeRecords(titles?.map(e => ({data: {[schema.propertyName]: e}})) ?? [], schema)
                            : (titles ?? []);

                        // Display the counts for the inner groups in their respective columns
                        // end with y -> ies, end with s -> es, else add s
                        const pluralName = schema.title.endsWith('y') ? `${schema.title.slice(0, -1)}ies` : schema.title.endsWith('s') ? `${schema.title}es` : `${schema.title}s`;
                        if (titleGroups.length <= 5 && !!titles) {
                            if (schema.type === ProductPropertyGroupRecordLeafTypes.range) {
                                cells[schema.propertyName] =
                                    <p {...dataGridCellTextProps} className={'text-xs font-weight-semi-bold grey-3'}>
                                        {
                                            titleGroups.map(e => {
                                                const vals = e.map(f => f.data[schema.propertyName])
                                                if (vals.length === 1) {
                                                    return vals[0];
                                                }
                                                return `${vals[0]}...${vals[vals.length - 1]}`;
                                            }).join(', ')
                                        }
                                    </p>;
                            } else {
                                cells[schema.propertyName] =
                                    <p {...dataGridCellTextProps} className={'text-xs font-weight-semi-bold grey-3'}>
                                        {titles?.map(e => (e === null && !schema.isRequired) ? schema.nullReplacement : e ?? "").join(', ') ?? ""}
                                    </p>;
                            }
                        } else {
                            cells[schema.propertyName] =
                                <p {...dataGridCellTextProps} className={'text-xs font-weight-semi-bold grey-3'}>
                                    {`${count} ${count === 1 ? schema.title : pluralName}`}
                                </p>;
                        }
                    } else {
                        // For parent groups, leave the column empty
                        cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                    }
                }
            }

            // Create a flat object for the current group or record
            flatArray.push({
                key: isLeaf
                    ? Array.isArray(group.value)
                        ? `leaves-${group.value.map(e => e.id).join('-')}`
                        : `leaf-${defaultRecord ? group.value.content : group.value}`
                    : group.commonId?.concat(forceLeaf ? '-leaves' : '') ?? createUUId(),
                data: group,
                cells: cells,
            });

            // If the group has children, recursively process them
            if (!isLeaf) {
                const newParents = [...parents, {value: group.value, propertyName: group.schema.propertyName}];
                if (group.records && !!group.records.length) {
                    const recordsSchema = group.schema.nextPropertyName ? structureSchema.find(e => e.propertyName === group.schema.nextPropertyName) : null;
                    switch (recordsSchema?.type) {
                        case ProductPropertyGroupRecordLeafTypes.range: {
                            const rangeRecords = groupRangeRecords(group.records, recordsSchema);
                            rangeRecords.forEach(rangeRecords => processGroup(
                                {
                                    parentCommonId: group.commonId,
                                    schema: recordsSchema,
                                    value: rangeRecords,
                                    approved: group.approved,
                                    counts: {},
                                },
                                newParents,
                                true,
                            ));
                            break;
                        }
                        default: {
                            group.records.forEach(record => processGroup({
                                commonId: group.commonId,
                                schema: recordsSchema,
                                value: record,
                                approved: group.approved,
                                counts: {},
                            }, newParents, true));
                            break;
                        }
                    }
                } else if (group.children && !!group.children.length) {
                    group.children.forEach(child => processGroup(child, newParents));
                }
            }
        }

        // Start processing the root groups
        groupedRecords.forEach(group => processGroup(group));

        return flatArray;
    }, [groupRangeRecords, groupedRecords, structureSchema])

    /**@type {DataGridRowGroup[]} */
    const dataGridGroups = useMemo(() => {
        return structureSchema.map(e => ({
            columnName: `__${e.propertyName}`,
            showOnColumn: e.propertyName,
        }))
    }, [structureSchema])

    /**
     * Expands All the rows of the data-grid.
     * @type {(function())}
     */
    const expandAll = useCallback(async () => {
        setExpandingAll(true);
        const dataGrid = document.querySelector('.property-group-data-grid');
        for (let i = 0; i < structureSchema.length; i++) {
            const toggleButtons = dataGrid?.querySelectorAll('.data-grid-group-toggler-button:not(.open):not(.invisible)');
            for (const toggleButton of toggleButtons) {
                toggleButton.click();
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        setExpandingAll(false);
    }, [structureSchema])

    /**
     * Collapses All the rows of the data-grid.
     * @type {(function())}
     */
    const collapseAll = useCallback(async () => {
        setCollapsingAll(true);
        const dataGrid = document.querySelector('.property-group-data-grid');
        for (let i = 0; i < structureSchema.length; i++) {
            const toggleButtons = dataGrid?.querySelectorAll('.data-grid-group-toggler-button.open:not(.invisible)');
            for (const toggleButton of toggleButtons) {
                toggleButton.click();
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        setCollapsingAll(false);
    }, [structureSchema])

    return (
        <>
            <div className={'d-flex justify-content-between my-3 mx-3 align-items-center'}>
                <div className={'d-flex align-items-center'} style={{gap: 4}}>
                    {
                        !(loadingSchema || loadingRecords) &&
                        <>
                            <Button
                                variant={'outlined'}
                                color={'secondary'}
                                className={'d-flex align-items-center'}
                                disabled={expandingAll || collapsingAll}
                                onClick={() => expandAll()}
                            >
                                <UnfoldMoreRounded className={'icon small'}/>
                                Expand all rows
                            </Button>
                            <Button
                                variant={'outlined'}
                                color={'secondary'}
                                className={'d-flex align-items-center'}
                                disabled={expandingAll || collapsingAll}
                                onClick={() => collapseAll()}
                            >
                                <UnfoldLessRounded className={'icon small'}/>
                                Collapse all rows
                            </Button>
                        </>
                    }
                </div>
            </div>
            <Box sx={{
                '.data-grid-loading-container': {
                    marginTop: `0 !important`,
                },
            }}>
                {
                    structureSchema.length > 1 &&
                    <PartnerDataGrid
                        initialLayoutState={{columns: columns,}}
                        classNames={{toolbar: 'pt-0', container: 'property-group-data-grid'}}
                        rows={dataGridRows}
                        columns={columns}
                        hideToolbar
                        hideFooter
                        state={{
                            loading: {state: loadingRecords || loadingSchema},
                            group: dataGridGroups,
                        }}
                    />
                }
            </Box>
        </>
    );

}

export default PropertyGroupInformationSection;